import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Building2,
  Check,
  FileCheck,
  Leaf,
  Microscope,
  Plus,
  Scissors,
  Search,
  Sprout,
  type LucideIcon,
} from 'lucide-react';
import { z } from 'zod';
import { Button, Card, Field, Input, Modal } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { api, ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  ComentariosSection,
  DetailDrawer,
  KanbanBoard,
  useKanban,
  type DetailTabSpec,
} from '@/modules/kanban';
import { HistoricoSection } from '@/modules/credito/components/HistoricoSection';
import { AMBIENTAL_PIPES } from '@/modules/ambiental/data';
import {
  useBuscarClientes,
  useCriarClienteNovo,
  useImoveisDoCliente,
  type ClienteBusca,
  type ImovelResumo,
} from '@/modules/ambiental/api';
import {
  ambientalCardFieldMap,
  ambientalDetailFieldMap,
} from '@/modules/ambiental/fieldMap';

const TRIAGEM_SERVICOS: { value: string; label: string; icon: LucideIcon }[] = [
  { value: 'ambiental_car', label: 'CAR', icon: Leaf },
  { value: 'ambiental_pra', label: 'PRA', icon: Sprout },
  { value: 'ambiental_desembargo', label: 'Desembargo', icon: FileCheck },
  { value: 'ambiental_limpeza', label: 'Limpeza de pastagem', icon: Scissors },
  { value: 'ambiental_licenciamento', label: 'Licenciamento', icon: Building2 },
  { value: 'ambiental_estudos_tecnicos', label: 'Laudos e análises', icon: Microscope },
];
const LICENCIAMENTO_ETAPAS = [
  { value: 'LP', label: 'LP' },
  { value: 'LI', label: 'LI' },
  { value: 'LO', label: 'LO' },
];
const ESTUDOS_TECNICOS_TIPOS = [
  { value: 'laudo_parecer', label: 'Laudo / Parecer técnico' },
  { value: 'analise_multitemporal', label: 'Análise geoespacial multitemporal' },
];
const LICENCIAMENTO_MACRO_PIPE = 'ambiental_licenciamento';
const ESTUDOS_TECNICOS_MACRO_PIPE = 'ambiental_estudos_tecnicos';

const AMBIENTAL_TABS: DetailTabSpec[] = [
  { id: 'cadastro', label: 'Cadastro' },
  {
    id: 'historico',
    label: 'Histórico',
    slot: (cardId) => <HistoricoSection cardId={cardId} dominio="ambiental" />,
  },
  {
    id: 'comentarios',
    label: 'Comentários',
    slot: (cardId) => <ComentariosSection dominio="ambiental" cardId={cardId} />,
  },
];

function isLicenciamentoMacro(pipe: string) {
  return pipe === LICENCIAMENTO_MACRO_PIPE;
}

function isEstudosTecnicosMacro(pipe: string) {
  return pipe === ESTUDOS_TECNICOS_MACRO_PIPE;
}

function useCreateTriagemAmbiental() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      cliente_cpf: string;
      imovel_id: number | null;
      servico_tipo: string;
      licenciamento_etapa: string | null;
      estudo_tecnico_tipo: string | null;
      observacoes: string | null;
      municipio: string | null;
    }) =>
      api('/api/v2/processos-ambiental', {
        method: 'POST',
        body: {
          tipo_processo: 'triagem_ambiental',
          ...body,
        },
        schema: z.record(z.unknown()),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['kanban', 'ambiental', 'triagem_ambiental'] });
      await qc.invalidateQueries({ queryKey: ['kanban', 'ambiental', 'detail'] });
    },
  });
}


function SecLabel({ children, chip }: { children: string; chip?: string | undefined }) {
  return (
    <div className="flex items-center gap-1.5">
      <p className="text-[0.68rem] font-medium uppercase tracking-[0.08em] text-text-muted">
        {children}
      </p>
      {chip && (
        <span className="rounded-full bg-surface2 px-1.5 py-px text-[0.62rem] font-medium text-text-muted">
          {chip}
        </span>
      )}
    </div>
  );
}

const iniciaisDe = (nome: string) =>
  nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

const resumoImovel = (im: ImovelResumo) => {
  const partes = [
    im.matricula ? `Matrícula ${im.matricula}` : null,
    im.area_total_ha != null ? `${im.area_total_ha.toFixed(1).replace('.', ',')} ha` : null,
    im.municipio ? `${im.municipio}${im.uf ? `/${im.uf}` : ''}` : null,
  ].filter(Boolean);
  return partes.join(' · ');
};

type ModoCliente = 'ativo' | 'novo';

function NovaTriagemAmbientalModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateTriagemAmbiental();
  const criarCliente = useCriarClienteNovo();

  const [servicoTipo, setServicoTipo] = useState('');
  const [licenciamentoEtapa, setLicenciamentoEtapa] = useState('');
  const [estudoTecnicoTipo, setEstudoTecnicoTipo] = useState('');
  const [modo, setModo] = useState<ModoCliente>('ativo');
  const [busca, setBusca] = useState('');
  const [cliente, setCliente] = useState<ClienteBusca | null>(null);
  const [imovelId, setImovelId] = useState<number | null>(null);
  const [novoNome, setNovoNome] = useState('');
  const [novoCpf, setNovoCpf] = useState('');
  const [novoTelefone, setNovoTelefone] = useState('');
  const [novoMunicipio, setNovoMunicipio] = useState('');
  const [novoUf, setNovoUf] = useState('');
  const [imDenominacao, setImDenominacao] = useState('');
  const [imMatricula, setImMatricula] = useState('');
  const [imMunicipio, setImMunicipio] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setServicoTipo('');
    setLicenciamentoEtapa('');
    setEstudoTecnicoTipo('');
    setModo('ativo');
    setBusca('');
    setCliente(null);
    setImovelId(null);
    setNovoNome('');
    setNovoCpf('');
    setNovoTelefone('');
    setNovoMunicipio('');
    setNovoUf('');
    setImDenominacao('');
    setImMatricula('');
    setImMunicipio('');
    setObservacoes('');
    setError(null);
  }, [open]);

  const buscaQ = useBuscarClientes(cliente ? '' : busca);
  const imoveisQ = useImoveisDoCliente(modo === 'ativo' ? (cliente?.cpf ?? null) : null);
  const imoveis = imoveisQ.data ?? [];
  const imovelEscolhido = imoveis.find((i) => i.id === imovelId) ?? null;
  const enviando = create.isPending || criarCliente.isPending;

  async function handleSubmit() {
    setError(null);
    if (!servicoTipo) return setError('Selecione o serviço.');
    if (servicoTipo === LICENCIAMENTO_MACRO_PIPE && !licenciamentoEtapa) {
      return setError('Selecione a etapa do licenciamento.');
    }
    if (servicoTipo === ESTUDOS_TECNICOS_MACRO_PIPE && !estudoTecnicoTipo) {
      return setError('Selecione o subtipo técnico.');
    }

    try {
      let cpf: string;
      let municipio: string | null;
      let obs = observacoes.trim();

      if (modo === 'ativo') {
        if (!cliente) return setError('Busque e selecione o cliente.');
        cpf = cliente.cpf;
        // imóvel escolhido é a fonte do município — não redigitar o que a base já sabe
        municipio = imovelEscolhido?.municipio ?? cliente.municipio ?? null;
      } else {
        if (!novoNome.trim()) return setError('Informe o nome do cliente.');
        if (!novoCpf.trim()) return setError('Informe o CPF do cliente.');
        const criado = await criarCliente.mutateAsync({
          cpf: novoCpf.trim(),
          nome: novoNome.trim(),
          telefone: novoTelefone.trim() || null,
          municipio: novoMunicipio.trim() || null,
          uf: novoUf.trim().toUpperCase() || null,
        });
        cpf = criado.cpf;
        municipio = novoMunicipio.trim() || null;
        // imóvel informado vira texto do levantamento: criar registro de imóvel
        // aqui geraria cadastro fantasma e inflaria os hectares dos painéis.
        const partes = [
          imDenominacao.trim(),
          imMatricula.trim() ? `Matrícula ${imMatricula.trim()}` : '',
          imMunicipio.trim(),
        ].filter(Boolean);
        if (partes.length) {
          obs = [obs, `Imóvel informado: ${partes.join(' · ')}`].filter(Boolean).join('\n\n');
        }
      }

      await create.mutateAsync({
        cliente_cpf: cpf,
        imovel_id: modo === 'ativo' ? imovelId : null,
        servico_tipo: servicoTipo,
        licenciamento_etapa: servicoTipo === LICENCIAMENTO_MACRO_PIPE ? licenciamentoEtapa : null,
        estudo_tecnico_tipo: servicoTipo === ESTUDOS_TECNICOS_MACRO_PIPE ? estudoTecnicoTipo : null,
        municipio,
        observacoes: obs || null,
      });
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao criar triagem ambiental.');
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Nova triagem"
      icon={<Leaf size={20} strokeWidth={1.8} />}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={enviando}>
            {enviando ? 'Criando…' : 'Criar triagem'}
          </Button>
        </>
      }
    >
      <Stack gap="lg">
        {/* SERVIÇO — as 6 opções à vista; a escolha define a esteira inteira */}
        <Stack gap="sm">
          <SecLabel>Serviço</SecLabel>
          <div className="grid grid-cols-3 gap-2.5">
            {TRIAGEM_SERVICOS.map((s) => {
              const Icon = s.icon;
              const on = servicoTipo === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setServicoTipo(s.value)}
                  className={cn(
                    'flex h-[84px] flex-col items-start gap-2 rounded-[12px] p-3 text-left transition-colors',
                    on
                      ? 'bg-accent'
                      : 'border border-border bg-surface2 hover:bg-surface3',
                  )}
                >
                  <Icon
                    size={18}
                    strokeWidth={1.8}
                    className={on ? 'text-[var(--on-solid)]' : 'text-text-muted'}
                  />
                  <span
                    className={cn(
                      'text-[0.78rem] leading-tight',
                      on ? 'font-semibold text-[var(--on-solid)]' : 'font-medium text-text-2',
                    )}
                  >
                    {s.label}
                  </span>
                </button>
              );
            })}
          </div>
          {servicoTipo === LICENCIAMENTO_MACRO_PIPE && (
            <div className="flex items-center gap-2 pt-0.5">
              <span className="text-[0.78rem] text-text-2">Etapa:</span>
              {LICENCIAMENTO_ETAPAS.map((e) => (
                <button
                  key={e.value}
                  type="button"
                  onClick={() => setLicenciamentoEtapa(e.value)}
                  className={cn(
                    'rounded-full px-3 py-1 text-[0.75rem] font-medium transition-colors',
                    licenciamentoEtapa === e.value
                      ? 'bg-[var(--pipe-active)] text-[var(--pipe-chip-fg)]'
                      : 'bg-surface2 text-text-muted hover:text-text-2',
                  )}
                >
                  {e.label}
                </button>
              ))}
            </div>
          )}
          {servicoTipo === ESTUDOS_TECNICOS_MACRO_PIPE && (
            <div className="flex flex-wrap items-center gap-2 pt-0.5">
              <span className="text-[0.78rem] text-text-2">Subtipo:</span>
              {ESTUDOS_TECNICOS_TIPOS.map((e) => (
                <button
                  key={e.value}
                  type="button"
                  onClick={() => setEstudoTecnicoTipo(e.value)}
                  className={cn(
                    'rounded-full px-3 py-1 text-[0.75rem] font-medium transition-colors',
                    estudoTecnicoTipo === e.value
                      ? 'bg-[var(--pipe-active)] text-[var(--pipe-chip-fg)]'
                      : 'bg-surface2 text-text-muted hover:text-text-2',
                  )}
                >
                  {e.label}
                </button>
              ))}
            </div>
          )}
        </Stack>

        {/* CLIENTE */}
        <Stack gap="sm">
          <SecLabel>Cliente</SecLabel>
          <div className="inline-flex w-fit gap-0.5 rounded-[10px] bg-surface2 p-[3px]">
            {(['ativo', 'novo'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setModo(m);
                  setError(null);
                }}
                className={cn(
                  'rounded-[8px] px-3.5 py-1.5 text-[0.78rem] transition-colors',
                  modo === m
                    ? 'bg-elevated font-semibold text-text shadow-[0_1px_2px_rgba(0,0,0,0.08)]'
                    : 'font-medium text-text-muted hover:text-text-2',
                )}
              >
                {m === 'ativo' ? 'Cliente ativo' : 'Novo cliente'}
              </button>
            ))}
          </div>

          {modo === 'ativo' ? (
            cliente ? (
              <button
                type="button"
                onClick={() => {
                  setCliente(null);
                  setImovelId(null);
                }}
                className="flex w-full items-center gap-2.5 rounded-[12px] bg-[var(--pipe-active)] p-2.5 text-left"
              >
                <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-elevated text-[0.72rem] font-semibold text-[var(--pipe-chip-fg)]">
                  {iniciaisDe(cliente.nome)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[0.85rem] font-medium text-text">
                    {cliente.nome}
                  </span>
                  <span className="block truncate text-[0.75rem] text-[var(--pipe-chip-fg)]">
                    {[cliente.cpf, cliente.municipio && `${cliente.municipio}${cliente.uf ? `/${cliente.uf}` : ''}`]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <Check size={15} strokeWidth={2} className="shrink-0 text-[var(--pipe-chip-fg)]" />
              </button>
            ) : (
              <Stack gap="xs">
                <div className="relative">
                  <Search
                    size={15}
                    strokeWidth={1.8}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                  />
                  <Input
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar por nome ou CPF"
                    className="pl-9"
                    autoFocus
                  />
                </div>
                {busca.trim().length >= 2 && (
                  <div className="flex flex-col gap-1">
                    {buscaQ.isLoading && <p className="text-[0.75rem] text-text-muted">Buscando…</p>}
                    {buscaQ.data?.length === 0 && (
                      <p className="text-[0.75rem] text-text-muted">
                        Nenhum cliente encontrado. Use “Novo cliente” para cadastrar.
                      </p>
                    )}
                    {buscaQ.data?.slice(0, 5).map((c) => (
                      <button
                        key={c.cpf}
                        type="button"
                        onClick={() => setCliente(c)}
                        className="flex items-center gap-2.5 rounded-[10px] bg-surface2 p-2 text-left hover:bg-surface3"
                      >
                        <span className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-elevated text-[0.65rem] font-semibold text-text-2">
                          {iniciaisDe(c.nome)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[0.8rem] font-medium text-text">
                            {c.nome}
                          </span>
                          <span className="block truncate text-[0.72rem] text-text-muted">
                            {[c.cpf, c.municipio].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </Stack>
            )
          ) : (
            <Stack gap="sm">
              <Field label="Nome completo" htmlFor="amb-novo-nome" required>
                <Input
                  id="amb-novo-nome"
                  value={novoNome}
                  onChange={(e) => setNovoNome(e.target.value)}
                  placeholder="Ex.: Noeci Scremin"
                />
              </Field>
              <div className="grid grid-cols-2 gap-2.5">
                <Field label="CPF" htmlFor="amb-novo-cpf" required>
                  <Input
                    id="amb-novo-cpf"
                    value={novoCpf}
                    onChange={(e) => setNovoCpf(e.target.value)}
                    placeholder="000.000.000-00"
                  />
                </Field>
                <Field label="Telefone" htmlFor="amb-novo-tel">
                  <Input
                    id="amb-novo-tel"
                    value={novoTelefone}
                    onChange={(e) => setNovoTelefone(e.target.value)}
                    placeholder="(69) 9 9999-9999"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-[1fr_88px] gap-2.5">
                <Field label="Município" htmlFor="amb-novo-mun">
                  <Input
                    id="amb-novo-mun"
                    value={novoMunicipio}
                    onChange={(e) => setNovoMunicipio(e.target.value)}
                    placeholder="Ex.: Pimenteiras do Oeste"
                  />
                </Field>
                <Field label="UF" htmlFor="amb-novo-uf">
                  <Input
                    id="amb-novo-uf"
                    value={novoUf}
                    onChange={(e) => setNovoUf(e.target.value)}
                    placeholder="RO"
                    maxLength={2}
                  />
                </Field>
              </div>
              <p className="text-[0.72rem] text-text-muted">
                O cliente será criado no cadastro ao salvar a triagem.
              </p>
            </Stack>
          )}
        </Stack>

        {/* IMÓVEL */}
        {modo === 'ativo' ? (
          cliente && (
            <Stack gap="sm">
              <SecLabel chip={imoveisQ.isLoading ? undefined : `${imoveis.length} na base`}>
                Imóvel
              </SecLabel>
              {imoveisQ.isLoading ? (
                <p className="text-[0.75rem] text-text-muted">Carregando imóveis…</p>
              ) : imoveis.length === 0 ? (
                <p className="text-[0.75rem] text-text-muted">
                  Esse cliente não tem imóvel cadastrado. A triagem pode seguir sem imóvel definido.
                </p>
              ) : (
                <>
                  <div className="flex flex-col gap-1.5">
                    {imoveis.map((im) => {
                      const on = imovelId === im.id;
                      return (
                        <button
                          key={im.id}
                          type="button"
                          onClick={() => setImovelId(on ? null : im.id)}
                          className={cn(
                            'flex items-center gap-2.5 rounded-[10px] p-2.5 text-left transition-colors',
                            on ? 'bg-[var(--pipe-active)]' : 'bg-surface2 hover:bg-surface3',
                          )}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[0.8rem] font-medium text-text">
                              {im.denominacao ?? `Imóvel ${im.id}`}
                            </span>
                            <span
                              className={cn(
                                'block truncate text-[0.72rem]',
                                on ? 'text-[var(--pipe-chip-fg)]' : 'text-text-muted',
                              )}
                            >
                              {resumoImovel(im)}
                            </span>
                          </span>
                          {on && (
                            <Check
                              size={15}
                              strokeWidth={2}
                              className="shrink-0 text-[var(--pipe-chip-fg)]"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[0.72rem] text-text-muted">
                    Nenhum destes? A triagem pode seguir sem imóvel definido.
                  </p>
                </>
              )}
            </Stack>
          )
        ) : (
          <Stack gap="sm">
            <SecLabel chip="opcional">Imóvel</SecLabel>
            <Field label="Denominação" htmlFor="amb-im-den">
              <Input
                id="amb-im-den"
                value={imDenominacao}
                onChange={(e) => setImDenominacao(e.target.value)}
                placeholder="Ex.: Sítio Paraná 1"
              />
            </Field>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Matrícula" htmlFor="amb-im-mat">
                <Input
                  id="amb-im-mat"
                  value={imMatricula}
                  onChange={(e) => setImMatricula(e.target.value)}
                  placeholder="Ex.: 0011"
                />
              </Field>
              <Field label="Município do imóvel" htmlFor="amb-im-mun">
                <Input
                  id="amb-im-mun"
                  value={imMunicipio}
                  onChange={(e) => setImMunicipio(e.target.value)}
                  placeholder="Ex.: Pimenteiras do Oeste"
                />
              </Field>
            </div>
            <p className="text-[0.72rem] text-text-muted">
              Cadastro completo do imóvel fica para depois do aceite do orçamento.
            </p>
          </Stack>
        )}

        {/* CONTEXTO */}
        <Stack gap="sm">
          <SecLabel chip="opcional">Contexto</SecLabel>
          <Field label="Levantamento superficial" htmlFor="amb-triagem-obs">
            <textarea
              id="amb-triagem-obs"
              className="resize-none rounded-[10px] border-0 bg-surface2 px-3 py-2.5 text-sm text-text outline-none placeholder:text-text-muted focus:ring-2 focus:ring-accent-soft"
              rows={3}
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Ex.: cliente precisa regularizar CAR e quer orçamento inicial."
            />
          </Field>
        </Stack>

        {error && <p className="text-sm text-danger">{error}</p>}
      </Stack>
    </Modal>
  );
}

function PipeSelectorCard({
  title,
  pipes,
  selected,
  onSelect,
}: {
  title: string;
  pipes: typeof AMBIENTAL_PIPES;
  selected: string;
  onSelect: (pipeId: string) => void;
}) {
  return (
    <Card padding="md" className="border-0 bg-surface2 shadow-none">
      <div className="flex flex-col gap-4">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-text-muted">{title}</p>
        <div className="flex flex-wrap gap-3.5">
          {pipes.map((pipe) => {
            const Icon = pipe.icon;
            const isSelected = selected === pipe.id;
            const isParent = pipe.kind === 'parent';
            const isCar = pipe.id === 'ambiental_car';
            const chipIsPai = isCar;
            return (
              <button
                key={pipe.id}
                type="button"
                onClick={() => onSelect(pipe.id)}
                className={cn(
                  'flex h-[108px] w-[144px] flex-col items-start gap-[7px] rounded-[7px] p-[9px] text-left transition-colors',
                  isSelected
                    ? 'bg-[var(--pipe-active)]'
                    : 'bg-surface shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:bg-[var(--pipe-active)] hover:shadow-none',
                )}
              >
                <span className="flex h-[27px] w-[27px] items-center justify-center rounded-[7px] bg-[var(--accent)]">
                  <Icon size={13} strokeWidth={1.6} className="text-[var(--on-solid)]" />
                </span>
                <span className="line-clamp-2 text-[0.82rem] font-semibold leading-tight text-text">
                  {pipe.label}
                </span>
                {!isParent && (
                  <span
                    className={cn(
                      'mt-auto inline-flex items-center rounded-full px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.12em]',
                      isSelected
                        ? 'bg-[var(--pipe-chip-bg)] text-[var(--pipe-chip-fg)]'
                        : 'bg-surface2 text-text-muted',
                    )}
                  >
                    {chipIsPai ? 'Card pai' : 'Card filho'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

export const Route = createFileRoute('/_authed/ambiental')({
  validateSearch: (search: Record<string, unknown>) => ({
    card: typeof search.card === 'string' && /^\d+$/.test(search.card) ? search.card : undefined,
    pipe: typeof search.pipe === 'string' && /^[a-z0-9_]+$/.test(search.pipe) ? search.pipe : undefined,
  }),
  component: AmbientalPage,
});

function parentPipeForDirectLink(pipe?: string) {
  if (['ambiental_lp', 'ambiental_li', 'ambiental_lo'].includes(pipe || '')) return 'ambiental_licenciamento';
  if (['ambiental_laudo_parecer', 'ambiental_analise_multitemporal'].includes(pipe || '')) return 'ambiental_estudos_tecnicos';
  return pipe || 'triagem_ambiental';
}

function AmbientalPage() {
  const search = Route.useSearch();
  const initialPipe = parentPipeForDirectLink(search.pipe);
  const [pipe, setPipe] = useState<string>(initialPipe);
  const [selectedCard, setSelectedCard] = useState<string | null>(search.card ?? null);
  const [detailPipe, setDetailPipe] = useState<string>(search.pipe ?? initialPipe);
  const [novaTriagemOpen, setNovaTriagemOpen] = useState(false);
  useEffect(() => {
    if (!search.card) return;
    const nextPipe = parentPipeForDirectLink(search.pipe);
    setPipe(nextPipe);
    setDetailPipe(search.pipe ?? nextPipe);
    setSelectedCard(search.card);
  }, [search.card, search.pipe]);
  const isLicenciamento = isLicenciamentoMacro(pipe);
  const isEstudosTecnicos = isEstudosTecnicosMacro(pipe);
  const isMacroPipe = isLicenciamento || isEstudosTecnicos;
  const { data, isLoading, error } = useKanban('ambiental', isMacroPipe ? 'triagem_ambiental' : pipe);
  const lpBoard = useKanban('ambiental', 'ambiental_lp');
  const liBoard = useKanban('ambiental', 'ambiental_li');
  const loBoard = useKanban('ambiental', 'ambiental_lo');
  const laudoBoard = useKanban('ambiental', 'ambiental_laudo_parecer');
  const multitemporalBoard = useKanban('ambiental', 'ambiental_analise_multitemporal');

  const licBoards = [
    { pipe: 'ambiental_lp', title: 'Licença Prévia (LP)', query: lpBoard },
    { pipe: 'ambiental_li', title: 'Licença de Instalação (LI)', query: liBoard },
    { pipe: 'ambiental_lo', title: 'Licença de Operação (LO)', query: loBoard },
  ] as const;
  const estudosBoards = [
    { pipe: 'ambiental_laudo_parecer', title: 'Laudo / Parecer técnico', query: laudoBoard },
    { pipe: 'ambiental_analise_multitemporal', title: 'Análise geoespacial multitemporal', query: multitemporalBoard },
  ] as const;
  const macroBoards = isLicenciamento ? licBoards : estudosBoards;
  const licLoading = licBoards.some((item) => item.query.isLoading);
  const licError = licBoards.find((item) => item.query.error)?.query.error;
  const estudosLoading = estudosBoards.some((item) => item.query.isLoading);
  const estudosError = estudosBoards.find((item) => item.query.error)?.query.error;
  const macroError = isLicenciamento ? licError : estudosError;
  const currentColumns = isMacroPipe
    ? macroBoards.find((item) => item.pipe === detailPipe)?.query.data?.colunas ?? []
    : data?.colunas ?? [];

  return (
    <PageShell
      title="Ambiental"
      titleSize="sm"
      headerClassName="!pt-4"
    >
      <Stack className="gap-5">
        <PipeSelectorCard
          title="Pipes técnicos"
          pipes={AMBIENTAL_PIPES}
          selected={pipe}
          onSelect={(id) => {
            setSelectedCard(null);
            setDetailPipe(id);
            setPipe(id);
          }}
        />

        {isMacroPipe ? (
          (isLicenciamento ? licLoading : estudosLoading) ? (
            <Card padding="lg">
              <p className="text-sm text-text-muted">
                {isLicenciamento ? 'Carregando licenciamento…' : 'Carregando laudos e análises técnicas…'}
              </p>
            </Card>
          ) : macroError ? (
            <Card padding="lg">
              <p className="text-sm text-danger">
                {macroError instanceof ApiError
                  ? macroError.message
                  : isLicenciamento
                    ? 'Falha ao carregar o macroserviço de licenciamento.'
                    : 'Falha ao carregar o macroserviço de laudos e análises técnicas.'}
              </p>
            </Card>
          ) : (
            <>
              {macroBoards.map((item) => item.query.data ? (
                <Card key={item.pipe} padding="md" className="border border-border/70 bg-surface/70">
                  <div className="mb-3 flex flex-col gap-1">
                    <p className="text-sm font-semibold text-text">{item.title}</p>
                    <p className="text-xs text-text-muted">
                      {isLicenciamento
                        ? 'Desdobramento interno de Licenciamento de Empreendimentos.'
                        : 'Desdobramento interno de Laudos e Análises Técnicas.'}
                    </p>
                  </div>
                  <KanbanBoard
                    data={item.query.data}
                    fieldMap={ambientalCardFieldMap}
                    dominio="ambiental"
                    pipe={item.pipe}
                    bareSearch
                    onCardSelect={(id) => {
                      setSelectedCard(id);
                      setDetailPipe(item.pipe);
                    }}
                  />
                </Card>
              ) : null)}
              <DetailDrawer
                dominio="ambiental"
                pipe={detailPipe}
                cardId={selectedCard}
                columns={currentColumns}
                cardFieldMap={ambientalCardFieldMap}
                detailFieldMap={ambientalDetailFieldMap}
                tabs={AMBIENTAL_TABS}
                onClose={() => setSelectedCard(null)}
              />
            </>
          )
        ) : isLoading ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">Carregando kanban…</p>
          </Card>
        ) : error ? (
          <Card padding="lg">
            <p className="text-sm text-danger">
              {error instanceof ApiError ? error.message : 'Falha ao carregar kanban.'}
            </p>
          </Card>
        ) : data ? (
          <>
            <KanbanBoard
              data={data}
              fieldMap={ambientalCardFieldMap}
              dominio="ambiental"
              pipe={pipe}
              bareSearch
              onCardSelect={(id) => {
                setSelectedCard(id);
                setDetailPipe(pipe);
              }}
            />
            <DetailDrawer
              dominio="ambiental"
              pipe={detailPipe}
              cardId={selectedCard}
              columns={currentColumns}
              cardFieldMap={ambientalCardFieldMap}
              detailFieldMap={ambientalDetailFieldMap}
              tabs={AMBIENTAL_TABS}
              onClose={() => setSelectedCard(null)}
            />
          </>
        ) : null}

        <div className="fixed bottom-6 left-4 z-40 md:left-[calc(var(--sidebar-w)+1.5rem)]">
          <Button
            variant="primary"
            onClick={() => setNovaTriagemOpen(true)}
            className="rounded-full shadow-[var(--shadow-over)]"
          >
            <Plus size={16} strokeWidth={1.8} />
            Nova triagem
          </Button>
        </div>

        <NovaTriagemAmbientalModal
          open={novaTriagemOpen}
          onOpenChange={setNovaTriagemOpen}
        />
      </Stack>
    </PageShell>
  );
}
